import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines, EASE } from "@/components/motion/Reveal";
import { CtaButton } from "@/components/brand/CtaButton";

const SHAPES = {
  arch: "rounded-t-[999px] rounded-b-[28px]",
  slant: "[clip-path:polygon(18%_0,100%_0,100%_100%,0_100%,0_16%)] rounded-br-[28px]",
  pill: "rounded-[999px]",
  notch: "[clip-path:polygon(0_0,100%_0,100%_82%,82%_100%,0_100%)]",
};

export const PageHero = ({ eyebrow, lines, intro, image, alt, actions = [], shape = "arch", testid = "page-hero", accentLine }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="grain relative overflow-hidden bg-ink pb-16 pt-32 sm:pt-40 lg:pb-24" data-testid={testid}>
      <div aria-hidden="true" className="pointer-events-none absolute -left-40 top-0 h-[520px] w-[520px] rounded-full bg-plum-3/40 blur-[120px]" />
      <div aria-hidden="true" className="pointer-events-none absolute right-0 top-1/3 h-[340px] w-[340px] rounded-full bg-gold/10 blur-[110px]" />
      <div className="shell relative z-[2] grid items-end gap-12 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="eyebrow flex items-center gap-3 text-gold">
            <span className="h-px w-10 bg-gold" /> {eyebrow}
          </motion.p>
          <MaskedLines
            lines={lines}
            testid={`${testid}-title`}
            className="mt-7 font-display text-[2.6rem] font-light leading-[1.02] tracking-[-0.02em] text-white sm:text-6xl lg:text-[4.6rem]"
            lineClassName={(i) => (i === accentLine ? "italic text-gold-metal shimmer" : "")}
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.55 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-white/75 md:text-lg"
          >
            {intro}
          </motion.p>
          {actions.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.7 }} className="mt-10 flex flex-col gap-3 sm:flex-row">
              {actions.map((a) => (
                <CtaButton key={a.label} to={a.to} variant={a.variant || "gold"} testid={a.testid}>{a.label}</CtaButton>
              ))}
            </motion.div>
          )}
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.3, ease: EASE, delay: 0.25 }}
          className="lg:col-span-5"
        >
          <div className={`relative h-[360px] overflow-hidden sm:h-[460px] lg:h-[560px] ${SHAPES[shape]}`}>
            <motion.img src={image} alt={alt} style={{ y: imgY }} className="absolute inset-0 h-[118%] w-full -translate-y-[6%] object-cover" />
            <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-ink/70 via-plum/10 to-transparent" />
            <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(233,208,138,0.25),transparent_55%)]" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
